import { useState, FormEvent } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import axios from 'axios'
import { setTokens, clearTokens } from '../lib/auth'
import { api } from '../lib/api'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export default function UserLogin() {
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const resp = await axios.post('/api/v1/auth/token/', { username, password })
      setTokens(resp.data.access, resp.data.refresh)
      const me = await api.get('/auth/me/')
      if (me.data.is_staff || me.data.is_superuser) {
        clearTokens()
        setError('Admin users must use the Admin Login page.')
        return
      }
      if (!me.data.is_active) {
        clearTokens()
        setError('Your account is inactive. Contact an administrator.')
        return
      }
      const from = (location.state as any)?.from?.pathname || '/portal'
      navigate(from, { replace: true })
    } catch (err: any) {
      if (err.response?.status === 401) setError('Invalid credentials')
      else setError(err.response?.data?.detail || 'Login failed — try again')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/90 to-primary flex items-center justify-center p-4">
      <Card className="w-full max-w-sm shadow-2xl">
        <CardHeader>
          <CardTitle>MooderERP</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username (email)</Label>
              <Input
                id="username" type="text" autoComplete="username" required
                value={username} onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2 rounded-md">
                {error}
              </div>
            )}
          </CardContent>

          <CardFooter className="flex flex-col items-stretch gap-3">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Admin?{' '}
              <Link to="/admin/login" className="text-primary hover:underline">Admin Login</Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
